import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Key, Truck, Download, Upload, Warehouse } from 'lucide-react';
import { api, API_BASE_URL, tokenStore } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { CreateCourierForm } from './CreateCourierForm';
import { ImportCourierDialog } from './ImportCourierDialog';
import { CourierApiConfigDialog } from './CourierApiConfigDialog';
import { PushWarehousesDialog } from './PushWarehousesDialog';
import { useAuth } from '@/hooks/useAuth';

interface Courier {
  id: number;
  name: string;
  code: string;
  brand?: string;
  serviceType: string;
  config?: { serviceTypeId?: string; loadType?: string; awbSeries?: string } | null;
  orderType: 'B2C' | 'B2B';
  isAggregator: boolean;
  tatDays?: number;
  addressLabelName?: string;
  weightCategory?: string;
  isActive: boolean;
  _count?: { credentials: number; merchantCouriers: number };
  /** Decided by the server (common/reverse-pickup.util.ts) — the same rule the booking uses. */
  reversePickup?: { supported: boolean; enabled: boolean; reverseOnly: boolean; reason: string | null };
}

/**
 * "Reverse pickup service" on / off for one courier. On = Initiate Return offers this courier. It
 * never turns the courier's ordinary deliveries into pickups — only an order booked through Initiate
 * Return goes out as a pickup. Where reverse pickup is not built (B2B, non-Delhivery) the switch is
 * shown disabled with the reason, rather than offering something the booking would get wrong.
 */
function ReverseSwitch({ courier }: { courier: Courier }) {
  const qc = useQueryClient();
  const rp = courier.reversePickup;
  const m = useMutation({
    mutationFn: async (enabled: boolean) => (await api.patch(`/couriers/${courier.id}/reverse-pickup`, { enabled })).data,
    onSuccess: (_r, enabled) => {
      qc.invalidateQueries({ queryKey: ['couriers'] });
      qc.invalidateQueries({ queryKey: ['return-couriers'] });
      toast.success(`Reverse pickup ${enabled ? 'enabled' : 'disabled'} for ${courier.name}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not change reverse pickup'),
  });
  if (!rp) return <span className="text-slate-300">—</span>;
  const on = rp.enabled;
  const disabled = m.isPending || (!rp.supported && !on);
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={() => m.mutate(!on)}
      title={!rp.supported ? (rp.reason ?? 'Not supported') : on ? 'Offered in Initiate Return — click to disable' : 'Click to offer this courier in Initiate Return'}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${on ? 'bg-success' : 'bg-slate-300'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

export function CouriersPage() {
  const [search, setSearch] = useState('');
  const [orderType, setOrderType] = useState<'all' | 'B2C' | 'B2B'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState<Courier | null>(null);
  const [apiConfigFor, setApiConfigFor] = useState<Courier | null>(null);
  const [pushFor, setPushFor] = useState<Courier | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/couriers/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `couriers-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err: any) {
      // Show a clear, actionable message instead of silently failing (esp. the 403 a sub-user hits
      // when their role hasn't been granted "Couriers → Export").
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('Permission denied: you can’t export couriers. Ask an administrator to enable “Couriers → Export” for your role.');
      } else if (status === 401) {
        toast.error('Your session has expired. Please sign in again and retry.');
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      setExporting(false);
      void API_BASE_URL; void tokenStore;
    }
  };

  const { data: couriers, isLoading } = useQuery({
    queryKey: ['couriers', { search, orderType }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (orderType !== 'all') params.orderType = orderType;
      const r = await api.get<Courier[]>('/couriers', { params });
      return r.data;
    },
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Couriers</h1>
          <p className="text-slate-500 text-sm mt-1">Manage courier partners and their multi-account credentials</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2 disabled:opacity-60">
            <Download size={16} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2">
            <Upload size={16} /> Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
            <Plus size={18} /> Add Courier
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input type="text" placeholder="Search courier name, code, brand..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <select value={orderType} onChange={(e) => setOrderType(e.target.value as 'all' | 'B2C' | 'B2B')}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
            <option value="all">All types</option>
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 whitespace-nowrap">Courier</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Code</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Type</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Service</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">API Service Type</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Weight</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Label Name</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">TAT</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Creds</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Merchants</th>
                <th className="text-left px-4 py-3 whitespace-nowrap" title="Offered in Initiate Return">Reverse Pickup</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={13} className="text-center py-12 text-slate-400">Loading...</td></tr>)}
              {!isLoading && couriers?.length === 0 && (
                <tr><td colSpan={13} className="text-center py-12 text-slate-400">
                  <Truck size={24} className="mx-auto mb-2 opacity-50" />
                  No couriers yet. Add your first courier to get started.
                </td></tr>
              )}
              {couriers?.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">ID #{c.id}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.code}</code></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.orderType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {c.orderType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{c.serviceType}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono whitespace-nowrap">{c.config?.serviceTypeId ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{c.weightCategory ? c.weightCategory.replace('KG', ' kg') : '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm">{c.addressLabelName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{c.tatDays ?? '—'} days</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="text-sm font-mono">{c._count?.credentials ?? 0}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="text-sm font-mono">{c._count?.merchantCouriers ?? 0}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap"><ReverseSwitch courier={c} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${c.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {c.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(c)}
                        className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-50 rounded" title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => setApiConfigFor(c)}
                        className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-50 rounded" title="API Config & Credentials"><Key size={14} /></button>
                      {isAdmin && (
                        <button onClick={() => setPushFor(c)}
                          className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-50 rounded" title="Push pickup locations (warehouses) to this courier"><Warehouse size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Courier" subtitle="Configure a new courier partner" maxWidth="2xl">
        <CreateCourierForm onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit Courier" subtitle={editing?.name} maxWidth="2xl">
        {editing && (
          <CreateCourierForm
            courierId={editing.id}
            defaultValues={{
              name: editing.name,
              code: editing.code,
              brand: editing.brand,
              serviceType: editing.serviceType as any,
              orderType: editing.orderType,
              isAggregator: editing.isAggregator,
              tatDays: editing.tatDays,
              addressLabelName: editing.addressLabelName,
              logoUrl: (editing as any).logoUrl ?? '',
              weightCategory: (editing.weightCategory as any) ?? '',
              apiServiceTypeId: editing.config?.serviceTypeId ?? '',
              apiLoadType: (editing.config?.loadType as any) ?? '',
              apiAwbSeries: editing.config?.awbSeries ?? '',
            }}
            onSuccess={() => setEditing(null)}
          />
        )}
      </Modal>

      <Modal isOpen={!!apiConfigFor} onClose={() => setApiConfigFor(null)}
        title="API Config & Credentials" subtitle={apiConfigFor ? `${apiConfigFor.name} · #${apiConfigFor.id}` : undefined} maxWidth="4xl">
        {apiConfigFor && (
          <CourierApiConfigDialog courier={apiConfigFor} onClose={() => setApiConfigFor(null)} />
        )}
      </Modal>

      <Modal isOpen={!!pushFor} onClose={() => setPushFor(null)}
        title="Push pickup locations" subtitle={pushFor ? `${pushFor.name} · #${pushFor.id}` : undefined} maxWidth="2xl">
        {pushFor && <PushWarehousesDialog courier={pushFor} onClose={() => setPushFor(null)} />}
      </Modal>

      <Modal isOpen={showImport} onClose={() => setShowImport(false)} title="Import Couriers from CSV" subtitle="Bulk-add or update couriers. Matching by code." maxWidth="2xl">
        <ImportCourierDialog onClose={() => setShowImport(false)} />
      </Modal>
    </div>
  );
}
